import { useRef, useState, useEffect, useCallback } from 'react'
import { Card, Col, Row, Dropdown } from 'react-bootstrap'
import { routeName } from 'routes/routes-name'
import { useHistory } from 'react-router-dom'
import { Box, MenuItem } from '@mui/material'
import moment from 'moment'
import _ from 'lodash'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** API */
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import ClinicApi, { BranchInterface } from 'api/master/clinic.api'

/** UTIL */
import { getBranch } from 'utils/app.utils'

/** COMPONENT */
import Loading from 'component/Loading'
import FucntionPrint from 'component/Print/print'
import HeaderCard from 'component/CardCustom/HeaderCard'
import FilterSelect from 'component/Select/FilterSelect'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import InputSecondNewRangePicker from 'component/Input/InputSecondNewRangePicker'
import ButtonCustom from 'component/Button/ButtonCustom'
import InputTextSearch from 'component/Input/InputTextSearch'
import WisdomToothList from './WisdomToothList'

/** STYLE */
import * as UseStyled from 'features/report/useStyled'

export interface WisdomToothSummary {
  sso: number
  nonSso: number
  total: number
}

export default function WisdomTooth() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const emptyRangePicker = ['', '']

  const [branches, setBranches] = useState<any[]>([])
  const [branchId, setBranchId] = useState<any>(getBranch())
  const [reportData, setReportData] = useState<any[]>([])
  const [exportData, setExportData] = useState<any[]>([])
  const [summary, setSummary] = useState<WisdomToothSummary>({ sso: 0, nonSso: 0, total: 0 })
  const [rowCount, setRowCount] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [rangeDate, setRangeDate] = useState<string[]>(emptyRangePicker)
  const [loading, setLoading] = useState<boolean>(false)
  const [search, setSearch] = useState('')
  const [fetch, setFetch] = useState<boolean>(false)

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = { page: 1, pageLimit: 200 }
    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'WISDOM_TOOTH_EXTRACTION') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })
    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch(() => { return })
    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [])

  const fetchReport = useCallback(() => {
    let condition: FindAllReportInterface = { page, pageLimit, branchId }
    if (sortType) condition = { ...condition, sortType }
    if (sortBy) condition = { ...condition, sortBy }
    if (rangeDate[0] && rangeDate[1]) condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }
    if (search) condition = { ...condition, search }

    ReportApi.reportWisdomToothExtraction(condition)
      .then(({ headers, data }) => {
        setReportData(data.rows)
        setRowCount(headers['x-total'])
        if (data.summary) setSummary(data.summary)
      })
      .catch(() => setLoading(false))
      .finally(() => {
        setLoading(false)
        setFetch(false)
      })
  }, [page, pageLimit, sortType, sortBy, fetch])

  const fetchReportExport = useCallback(() => {
    let condition: FindAllReportInterface = { page: 1, pageLimit: 10000, sortType, sortBy, branchId, export: 1 }
    if (search) condition = { ...condition, search }
    if (rangeDate[0] && rangeDate[1]) condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }

    ReportApi.reportWisdomToothExtraction(condition)
      .then(({ data }) => {
        setExportData(data.rows)
        if (data.summary) setSummary(data.summary)
      })
      .catch(() => setLoading(false))
      .finally(() => {
        setLoading(false)
        setFetch(false)
      })
  }, [page, pageLimit, sortType, sortBy, fetch])

  const filter = [
    branchId === 0 ? `${t(`REPORT.FILTER.ALL`)}` : `${_.get(_.find(branches, { branchId }), 'branchName' || 'branchNameEn')}`,
    rangeDate[0],
    rangeDate[1]
  ]

  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.WISDOM_TOOTH_EXTRACTION`)} ${rangeDate[0] ? `(${rangeDate[0]}-${rangeDate[1]})` : ''} `,
    sheetName: `${t(`REPORT.REPORT_NAME.WISDOM_TOOTH_EXTRACTION`)}`
  })

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  useEffect(() => {
    fetchReport()
  }, [fetchReport])

  useEffect(() => {
    fetchReportExport()
  }, [fetchReportExport])

  return (
    <div>
      <Card className={'border-0 h-100'}>
        <HeaderCard text={t(`REPORT.REPORT_NAME.WISDOM_TOOTH_EXTRACTION`)} />
        <Card.Body>
          <Card.Title className={'mb-0'}>
            <Row className="mx-0">
              <Col sm={6} md={4} xl={3} className={'pt-2 px-1'}>
                <InputTextSearch
                  keyInput={'search'}
                  label={t('REPORT.REPORT_NAME.SEARCH_WISDOM_TOOTH_EXTRACTION')}
                  value={search}
                  onchange={(event) => {
                    setSearch(event.target.value)
                    setPage(1)
                  }}
                  onClear={() => {
                    setSearch('')
                    setPage(1)
                  }}
                />
              </Col>
              <Col sm={6} md={4} xl={3} className={'pt-2 px-1'}>
                <FilterSelect
                  onchange={(e) => {
                    setBranchId(e.target.value)
                    setPage(1)
                  }}
                  renderValue={() => `${t('REPORT.FILTER.BRANCH')}: ${_.get(_.find(branches, { branchId }), 'branchName' || 'branchNameEn')} `}
                  label={''}
                  selectId="select-branch"
                  labelId="label-branch"
                  value={branchId}
                  options={_.map(branches, (b: BranchInterface) => (
                    <MenuItem key={b.branchId} value={b.branchId}>
                      {b.branchName || b.branchNameEn}
                    </MenuItem>
                  ))}
                  formControlStyle={{ maxWidth: 'unset !important' }}
                  classesOption="style-select-doctors"
                />
              </Col>
              <Col sm={6} md={4} xl={3} className={'pt-2 px-1'}>
                <InputSecondNewRangePicker
                  inputHeight={32}
                  value={rangeDate || emptyRangePicker}
                  onchange={(val: any) => {
                    if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                    else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                    setPage(1)
                  }}
                  label={''}
                />
              </Col>
              <Col sm={6} md={1} xl={1} className={'px-1'}>
                <ButtonCustom
                  onClick={() => {
                    setFetch(true)
                    setPage(1)
                  }}
                  textButton={t('INPUT.SEARCH')}
                  style={{ height: '30px', marginTop: '5px', width: '100%' }}
                />
              </Col>
              <Col md={4} xl={3} className={'d-xl-flex justify-content-xl-end ml-xl-auto pt-2 px-1'}>
                <UseStyled.DropdownDocument>
                  <Dropdown.Toggle id="dropdown-basic" className="ml-xl-auto">
                    {t('PRINT_DOCUMENT')}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={onDownload}>{t('Excel')}</Dropdown.Item>
                    <FucntionPrint
                      content={() => componentRef.current}
                      docTitle={t(`REPORT.REPORT_NAME.WISDOM_TOOTH_EXTRACTION`)}
                      removeIfram
                      trigger={() => <Dropdown.Item>{t('PDF')}</Dropdown.Item>}
                      style={`@page { size: 793.7007874px 1122.519685px;  margin: 0.5cm; } @media print { body { -webkit-print-color-adjust: exact; }`}
                    />
                  </Dropdown.Menu>
                </UseStyled.DropdownDocument>
              </Col>
            </Row>
            <Row className="mx-0 mt-2">
              <Col xs={12}>
                <UseStyled.SummaryBox>
                  <span className="me-3">
                    <strong>{t('REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.SSO_GROUP')}:</strong> {summary.sso}
                  </span>
                  <span className="me-3">
                    <strong>{t('REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.NON_SSO_GROUP')}:</strong> {summary.nonSso}
                  </span>
                  <span>
                    <strong>{t('REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.TOTAL')}:</strong> {summary.total}
                  </span>
                </UseStyled.SummaryBox>
              </Col>
            </Row>
          </Card.Title>
          <Box className={'mt-3 pl-xl-3 pr-xl-2 pb-4'}>
            <WisdomToothList
              filter={filter}
              page={page}
              pageLimit={pageLimit}
              rowCount={rowCount}
              sortBy={sortBy}
              sortType={sortType}
              data={reportData}
              summary={summary}
              onRequestSort={onRequestSort}
              handleChangeRowsPerPage={(limit: number) => setPageLimit(limit)}
              handleChangePage={(val: number) => setPage(val)}
              componentRef={componentRef}
              exportData={exportData}
            />
          </Box>
        </Card.Body>
      </Card>
      <Loading show={loading} type="fullLoading" />
    </div>
  )
}
